import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { LiverDirectoryActivity } from './liverDirectory'
import { buildLiverPayload, type LiverFormValues } from './liverForm'
import { hasInternalApi, internalApiGet, internalApiPatch, internalApiPost } from './internalApi'
import { supabase, type Liver } from './supabase'

type LiversResponse = {
  livers: Liver[]
}

type LiverResponse = {
  liver: Liver
}

type DirectoryCall = {
  liver_id: string
  called_at: string | null
}

type DirectoryLineImport = {
  liver_id: string
  created_at: string | null
}

type CallsResponse = {
  calls: DirectoryCall[]
}

type LineImportsResponse = {
  line_imports: DirectoryLineImport[]
}

/**
 * Fetches all livers, sorted by display_name.
 * Used by the global sidebar list.
 */
export function useLivers() {
  return useQuery<Liver[]>({
    queryKey: ['livers'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const response = await internalApiGet<LiversResponse>('/api/livers')
        return response.livers
      }

      const { data, error } = await supabase
        .from('livers')
        .select('*')
        .order('display_name', { ascending: true })
      if (error) throw error
      return (data ?? []) as Liver[]
    }
  })
}

/**
 * Fetches a single liver by id. Returns null if not found.
 */
export function useLiver(id: string | undefined) {
  return useQuery<Liver | null>({
    queryKey: ['liver', id],
    enabled: !!id,
    queryFn: async () => {
      if (!id) return null
      if (hasInternalApi()) {
        try {
          const response = await internalApiGet<LiverResponse>(`/api/livers/${id}`)
          return response.liver
        } catch (error) {
          if (error instanceof Error && /not found/i.test(error.message)) return null
          throw error
        }
      }

      const { data, error } = await supabase
        .from('livers')
        .select('*')
        .eq('id', id)
        .maybeSingle()
      if (error) throw error
      return data as Liver | null
    }
  })
}

export function useLiverDirectoryActivities() {
  return useQuery<LiverDirectoryActivity[]>({
    queryKey: ['livers', 'directory-activities'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const [calls, lineImports] = await Promise.all([
          internalApiGet<CallsResponse>('/api/calls?limit=1000'),
          internalApiGet<LineImportsResponse>('/api/line-imports?limit=1000')
        ])
        return [
          ...calls.calls.map(toCallActivity),
          ...lineImports.line_imports.map(toLineActivity)
        ]
      }

      const [callsResult, lineImportsResult] = await Promise.all([
        supabase
          .from('call_records')
          .select('liver_id,called_at')
          .order('called_at', { ascending: false })
          .limit(1000),
        supabase
          .from('line_imports')
          .select('liver_id,created_at')
          .order('created_at', { ascending: false })
          .limit(1000)
      ])
      if (callsResult.error) throw callsResult.error
      if (lineImportsResult.error) throw lineImportsResult.error
      return [
        ...((callsResult.data ?? []) as DirectoryCall[]).map(toCallActivity),
        ...((lineImportsResult.data ?? []) as DirectoryLineImport[]).map(toLineActivity)
      ]
    }
  })
}

export function useCreateLiver() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (values: LiverFormValues) => {
      const payload = buildLiverPayload(values)
      if (hasInternalApi()) {
        const response = await internalApiPost<LiverResponse>('/api/livers', payload)
        return response.liver
      }

      const { data, error } = await supabase
        .from('livers')
        .insert(payload)
        .select('*')
        .single()
      if (error) throw error
      return data as Liver
    },
    onSuccess: (liver) => {
      queryClient.invalidateQueries({ queryKey: ['livers'] })
      queryClient.setQueryData(['liver', liver.id], liver)
    }
  })
}

export function useUpdateLiver() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: LiverFormValues }) => {
      const payload = buildLiverPayload(values)
      if (hasInternalApi()) {
        const response = await internalApiPatch<LiverResponse>(`/api/livers/${id}`, payload)
        return response.liver
      }

      const { data, error } = await supabase
        .from('livers')
        .update(payload)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error
      return data as Liver
    },
    onSuccess: (liver) => {
      queryClient.invalidateQueries({ queryKey: ['livers'] })
      queryClient.setQueryData(['liver', liver.id], liver)
    }
  })
}

function toCallActivity(call: DirectoryCall): LiverDirectoryActivity {
  return {
    liver_id: call.liver_id,
    source: 'call',
    occurred_at: call.called_at
  }
}

function toLineActivity(lineImport: DirectoryLineImport): LiverDirectoryActivity {
  return {
    liver_id: lineImport.liver_id,
    source: 'line',
    occurred_at: lineImport.created_at
  }
}
